import { useState, useEffect } from 'react';

// Simple in-memory cache
const cache: Record<string, unknown> = {};
const pendingRequests: Record<string, Promise<unknown>> = {};

// Preload critical data files on app start
const PRELOAD_URLS = [
  '/data/services.json',
  '/data/industries.json',
  '/data/impact.json',
  '/data/how-it-works.json',
  '/data/why-punarvaa.json',
  '/data/clients.json',
  '/data/about.json',
];

export function preloadJsonData(): void {
  PRELOAD_URLS.forEach((url) => {
    if (!cache[url] && !pendingRequests[url]) {
      pendingRequests[url] = fetch(url)
        .then((res) => res.json())
        .then((data) => {
          cache[url] = data;
          delete pendingRequests[url];
          return data;
        })
        .catch(() => {
          delete pendingRequests[url];
        });
    }
  });
}

// Start preloading immediately when module loads
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    (window as Window).requestIdleCallback(() => preloadJsonData(), { timeout: 1000 });
  } else {
    setTimeout(preloadJsonData, 100);
  }
}

export function useJsonData<T>(url: string): { data: T | null; loading: boolean; error: Error | null } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (cache[url]) {
      setData(cache[url] as T);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchData() {
      try {
        let result: unknown;
        if (pendingRequests[url]) {
          result = await pendingRequests[url];
        } else {
          pendingRequests[url] = fetch(url).then((res) => {
            if (!res.ok) throw new Error(`Failed to fetch ${url}`);
            return res.json();
          });
          result = await pendingRequests[url];
          cache[url] = result;
          delete pendingRequests[url];
        }

        if (!cancelled) {
          setData(result as T);
          setLoading(false);
        }
      } catch (err) {
        delete pendingRequests[url];
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Unknown error'));
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { data, loading, error };
}
